import { differenceBy, filter, isEmpty, isEqual, map, reduce, size, some } from 'lodash'
import moment from 'moment-timezone'
import { FieldProps, getIn } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Column,
  Row,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  UseRowSelectInstanceProps,
  useSortBy,
  useTable,
} from 'react-table'

import { SuperEventMember } from 'super-events/types'
import { useSelectColumn } from 'core/table/table-hooks'
import { ExtendedPagedTable } from 'core/table/components'
import useEventSelectFieldColumns from 'super-events/hooks/useEventSelectFieldColumns'
import useEventsCollection from 'Event/scenes/events/hooks/useEventsCollection'
import { useDateRangeFilter } from 'filtering/hooks'
import { DATE_RANGE_NEXT_90_DAYS, getDateRangeFromPreset } from 'components/filter/DateRangePresets'
import { Loading } from 'civic-champs-shared'
import { OpportunityResponse } from 'Event/interfaces/interfaceCreateEditEvent'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { useEffectOnce } from 'react-use'

interface EventSelectFieldProps<FormValues = any> extends FieldProps<SuperEventMember[], FormValues> {}

const tableName = 'eventSelectorField'

const useFormatEventDate = () => {
  const organization = useCurrentOrg()
  return useCallback(
    (row: OpportunityResponse): string => moment.tz(row.startsAt, organization.timeZone).format('YYYY-MM-DD'),
    [organization.timeZone],
  )
}

const useGetRowId = () => {
  const format = useFormatEventDate()
  return useCallback(
    (row: OpportunityResponse, relativeIndex: number, parent?: Row<OpportunityResponse>): string =>
      `${row.opportunityId}_${format(row)}`,
    [format],
  )
}

const toSelectedRowIds = (events: SuperEventMember[]): { [id: string]: boolean } => {
  return reduce<SuperEventMember, { [id: string]: boolean }>(
    events,
    (memo: { [id: string]: boolean }, event: SuperEventMember) => {
      memo[`${event.opportunityId}_${event.date}`] = true
      return memo
    },
    {},
  )
}

const useMergeSelectionIntoValue = () => {
  const organization = useCurrentOrg()
  const formatEventDate = useFormatEventDate()

  return useCallback(
    (visibleRows: Row<OpportunityResponse>[], selectedRows: Row<OpportunityResponse>[], value: SuperEventMember[]) => {
      const inVisibleRows = (event: SuperEventMember) => {
        const key = `${event.opportunityId}_${event.date}`
        return some(
          visibleRows,
          (row: Row<OpportunityResponse>) => key === `${row.original.opportunityId}_${formatEventDate(row.original)}`,
        )
      }

      const retained = filter(value, event => !inVisibleRows(event))

      const newEvents: SuperEventMember[] = selectedRows.map(row => ({
        opportunityId: row.original.opportunityId,
        date: formatEventDate(row.original),
      }))

      return [...retained, ...newEvents]
    },
    [formatEventDate],
  )
}

const eventsChanged = (lhs: SuperEventMember[], rhs: SuperEventMember[]) =>
  size(rhs) != size(lhs) || differenceBy(lhs, rhs, event => `${event.opportunityId}_${event.date}`).length > 0

export const EventSelectField = ({ field, form, meta, ...props }: EventSelectFieldProps) => {
  const touched = getIn(form.touched, field.name)
  const error = getIn(form.errors, field.name)

  const organization = useCurrentOrg()
  const defaultDateRange = useMemo(() => {
    if (isEmpty(field.value)) return getDateRangeFromPreset(DATE_RANGE_NEXT_90_DAYS)

    const dates = map(field.value, event => moment.tz(event.date, organization.timeZone))
    return {
      startDate: moment.min(dates).startOf('day'),
      endDate: moment.max(dates).endOf('day'),
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [{ startDate, endDate }] = useDateRangeFilter({ key: tableName, defaultDateRange, forceDefault: true })
  const [{ events, loading }, operations, listeners] = useEventsCollection({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  })

  const initializedRef = useRef(false)
  useEffectOnce(() => {
    listeners.onSync(() => (initializedRef.current = true))
  })

  const columns = useEventSelectFieldColumns()
  const getRowId = useGetRowId()

  const table = useTable(
    {
      initialState: {
        // @ts-ignore
        globalFilter: '',
        selectedRowIds: toSelectedRowIds(field.value),
      },
      columns: columns as Column<OpportunityResponse>[],
      data: events,
      getRowId,
      autoResetSelectedRows: false,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useSelectColumn,
  )

  const mergeSelectionIntoValue = useMergeSelectionIntoValue()

  const selectedRows = (table as unknown as UseRowSelectInstanceProps<OpportunityResponse>).selectedFlatRows

  useEffect(() => {
    if (!initializedRef.current) return

    const newValue = mergeSelectionIntoValue(table.flatRows, selectedRows, field.value)

    if (eventsChanged(newValue, field.value)) {
      form.setFieldValue(field.name, newValue, true)
    }
  }, [table.flatRows, selectedRows, form.setFieldValue, field.name, form, mergeSelectionIntoValue, field.value])

  if (loading) return <Loading />

  return (
    <ExtendedPagedTable
      tableName={tableName}
      table={table}
      columns={columns}
      stackedSectionHeader
      title="Events"
      searchPlaceholder="Search Events"
      customDateKey={tableName}
      defaultDateRange={defaultDateRange}
      compactView
      expandable={false}
      useDateRange
      useGlobalSearch
      useFilters
      buttons={
        <p>
          {size(field.value)} events selected
          {selectedRows.length !== size(field.value) ? ` (${selectedRows.length} visible in table)` : ''}
        </p>
      }
    />
  )
}
